import React, { useCallback, useEffect, useState } from 'react';

import { Flex, Radio, Space, Tag } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio';

import { useSearchBoxView } from '../hooks/useSearchBoxView';
import { IBox } from '../models/box';
import SearchObject from '@/components/SearchObject';
import useToasts from '@/shared/hooks/useToasts';
import { MessageType } from '@/shared/hooks/useToasts.types';

interface ISearchBoxProps {
  disabled?: boolean;
  boxId?: string;
  setBoxId: (id: string) => void;
  selectedBox?: string;
  setSelectedBox?: (box: string | undefined) => void;
}

const getBoxLabel = (box: IBox): string => {
  if (box.thingName && box.lpUI) return `${box.thingName} (${box.lpUI})`;

  return box.thingName || box.lpUI;
};

const BoxDisplayName = ({ box }: { box: IBox }) => (
  <Flex gap="0.5rem" wrap="wrap">
    <strong>{getBoxLabel(box)}</strong>
    {box.productDescription && (
      <Tag color="green" bordered={false}>
        {box.productDescription}
      </Tag>
    )}
  </Flex>
);

export const SearchBox = ({
  disabled = false,
  boxId,
  setBoxId,
  selectedBox,
  setSelectedBox,
}: ISearchBoxProps) => {
  const addToast = useToasts((state) => state.addItem);

  const { searchResults, search, clearResults, hasSearched, isError } =
    useSearchBoxView();

  const [searchField, setSearchField] = useState<string>('');
  const [displayedResults, setDisplayedResults] = useState<IBox[]>([]);

  const handleFindBox = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm) return;

      search(searchTerm);
    },
    [search]
  );

  useEffect(() => {
    if (searchResults.length === 0) return;

    if (searchResults.length === 1) {
      const box = searchResults[0];
      setBoxId(box.id);
      const label = getBoxLabel(box);
      setSearchField(label);
      if (setSelectedBox) {
        setSelectedBox(label);
      }
      setDisplayedResults([]);

      return;
    }

    setDisplayedResults(searchResults);
  }, [searchResults, setBoxId, setSelectedBox]);

  useEffect(() => {
    if (!hasSearched) return;
    if (isError) return; // API errors are handled by useImperativeRequestWrapper
    if (searchResults.length > 0) return;

    addToast(
      {
        msg: 'No box found',
        type: MessageType.Error,
      },
      'root',
      {}
    );
  }, [searchResults, hasSearched, isError, addToast]);

  const handleBoxChoice = useCallback(
    (e: RadioChangeEvent) => {
      const selectedBoxItem = displayedResults.find(
        (res) => res.id === e.target.value
      );

      if (!selectedBoxItem) return;

      setBoxId(e.target.value as string);
      const label = getBoxLabel(selectedBoxItem);
      setSearchField(label);

      if (setSelectedBox) {
        setSelectedBox(label);
      }
    },
    [setBoxId, displayedResults, setSelectedBox]
  );

  useEffect(() => {
    setSearchField(selectedBox ? selectedBox : '');
  }, [selectedBox]);

  const handleSetBoxId = useCallback(
    (id: string) => {
      setBoxId(id);
      if (!id && setSelectedBox) {
        setSelectedBox(undefined);
      }
    },
    [setBoxId, setSelectedBox]
  );

  const handleSetSearchResults = useCallback(
    (results: IBox[]) => {
      setDisplayedResults(results);
      if (results.length === 0) {
        clearResults();
      }
    },
    [clearResults]
  );

  return (
    <>
      <SearchObject
        searchField={searchField}
        setSearchField={setSearchField}
        objectId={boxId}
        setObjectId={handleSetBoxId}
        disabled={disabled}
        placeholder="LPUI / SN..."
        searchLabel="Box (search by LPUI or Serial Number) - First 15 results displayed"
        foundLabel="Box"
        setSearchResults={handleSetSearchResults}
        handleFind={handleFindBox}
      />
      {displayedResults.length > 0 && (
        <Radio.Group onChange={handleBoxChoice}>
          <Space direction="vertical">
            {displayedResults.map((box) => (
              <Radio
                value={box.id}
                key={box.id}
                style={{
                  marginTop: '10px',
                }}
              >
                <BoxDisplayName box={box} />
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      )}
    </>
  );
};
